import EmptyView from '@components/common/emptyView';
import { Path } from '@components/routes';
import SourceTrackingPlanConfig from '@components/sources/source-view/source-tracking-plan/sourceTrackingPlanConfig';
import useModalView from '@hooks/useModalView';
import { ISourceDataStore } from '@stores/sourceDataStore';
import useStores from '@stores/useStores';
import Button from '@ui-library/button';
import Modal from '@ui-library/modal/custom';
import { Body } from '@ui-library/typography';
import Skeleton from 'antd/lib/skeleton/Skeleton';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

interface Props {
  source: ISourceDataStore;
  isAdmin: boolean;
}

export default observer(({ source, isAdmin }: Props) => {
  const { trackingPlanListStore } = useStores();
  const { trackingPlanConfig, id } = source;
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const handleUnlink = async () => {
    setLoading(true);
    await source.unlinkTrackingPlan();
    setLoading(false);
    showUnlinkModal(false);
  };

  const getModalContent = () => (
    <Body>
      Are you sure you want to unlink the <b>{source?.name}</b> source from this tracking plan?
    </Body>
  );

  const [unlinkModal, showUnlinkModal] = useModalView(
    <Modal
      size="md"
      title="Unlink Source"
      content={getModalContent()}
      onClose={() => {
        showUnlinkModal(false);
      }}
      saveLabel="Unlink"
      saveLabelBtnType="deletePrimary"
      onSave={handleUnlink}
      loading={loading}
    />,
  );

  const handleLinkSource = () => {
    history.push(Path.LINK_TRACKING_PLAN(id));
  };

  const getEmptyComponent = () => (
    <EmptyView
      headerText="You haven’t set up a Tracking Plan for this Source"
      subText="When you connect a Tracking Plan to this Source all of your Tracking Plan configurations will appear here."
      cta={isAdmin ? <Button name="Link a Tracking Plan" onClick={handleLinkSource} /> : null}
      svgName="empty_box"
    />
  );
  if (!trackingPlanConfig) {
    return getEmptyComponent();
  }
  const plan = trackingPlanListStore.getById(trackingPlanConfig!.trackingPlanId);
  if (!plan) {
    return <Skeleton active />;
  }
  return (
    <>
      {unlinkModal}
      <div className="m-b-lg">
        This source is currently connected to the tracking plan
        <Link to={Path.TRACKING_PLAN_DETAILS(plan.id)}>
          <Button textPrimary name={plan?.name} />
        </Link>
        {plan?.id}
      </div>

      <SourceTrackingPlanConfig trackingPlanConfig={trackingPlanConfig.config} isAdmin={isAdmin} />

      {isAdmin && (
        <Button
          data-testid="unlink-button"
          deleteOutlined
          name="Unlink Tracking Plan"
          onClick={() => showUnlinkModal(true)}
        />
      )}
    </>
  );
});
